import { ChangeEvent, FC, useCallback, useState } from "react";
import { Box, createStyles, makeStyles, Typography } from "@material-ui/core";
import { isEqual } from "lodash";
import { usePostHog } from "posthog-js/react";

import { LogInRequiredModal } from "components";
import { ResetButton, Switch } from "components";
import { UiParams } from "hooks/useQueryParams/types";
import { LISTING_DEAL_STATES } from "services/listings/constants";
import { EXCHANGE_FILTER_SORT } from "services/postHog/constants";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    filterResetContainer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(0.5),
    },
    filterByTitle: {
      textTransform: "uppercase",
      fontSize: "0.75rem",
      color: theme.custom.palette.gray.dark,
    },
  }),
);

interface ToggleFilterGroupProps {
  userId: number;
  hideResetButton: boolean;
  handleResetFilters: () => void;
  isDefaultUiParams: boolean;
  uiParams: UiParams;
  setUiParams: (setState: (uiParams: UiParams) => UiParams) => void;
  hideOnlyYourListings?: true;
  hideOnlySavedListings?: true;
  hideOnlyOfferedListings?: true;
  hideOnlyActiveOfferListings?: true;
  hideOnlyFilledListings?: true;
}

const ToggleFilterGroup: FC<ToggleFilterGroupProps> = ({
  userId,
  hideResetButton,
  handleResetFilters,
  isDefaultUiParams,
  uiParams,
  setUiParams,
  hideOnlyYourListings,
  hideOnlySavedListings,
  hideOnlyOfferedListings,
  hideOnlyActiveOfferListings,
  hideOnlyFilledListings,
}) => {
  const classes = useStyles();
  const posthog = usePostHog();
  const isAuthenticated = !!userId;

  const [isLoginRequiredModalOpen, setIsLoginRequiredModalOpen] = useState(
    false,
  );

  const handleToggleOnlySavedListings = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isAuthenticated) {
        posthog?.capture(EXCHANGE_FILTER_SORT, {
          actionType: e.target.checked ? "checked" : "unchecked",
          filterBy: "Only show my saved listings",
        });

        const updated = e.target.checked ? "1" : "0";
        setUiParams(prev => ({
          ...prev,
          page: { ...prev.page, value: "1" },
          onlyFavorited: {
            ...prev.onlyFavorited,
            value: updated,
          },
        }));
      } else {
        setIsLoginRequiredModalOpen(true);
      }
    },
    [isAuthenticated, posthog, setUiParams],
  );

  const handleToggleOnlyOfferedListings = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isAuthenticated) {
        posthog?.capture(EXCHANGE_FILTER_SORT, {
          actionType: e.target.checked ? "checked" : "unchecked",
          filterBy: "Only My Offers",
        });

        const updated = e.target.checked ? "1" : "0";
        setUiParams(prev => ({
          ...prev,
          page: { ...prev.page, value: "1" },
          onlyOffered: {
            ...prev.onlyOffered,
            value: updated,
          },
        }));
      } else {
        setIsLoginRequiredModalOpen(true);
      }
    },
    [isAuthenticated, posthog, setUiParams],
  );

  const handleToggleOnlyActiveOfferListings = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isAuthenticated) {
        posthog?.capture(EXCHANGE_FILTER_SORT, {
          actionType: e.target.checked ? "checked" : "unchecked",
          filterBy: "Only My Active Offers",
        });

        const updated = e.target.checked ? "1" : "0";
        setUiParams(prev => ({
          ...prev,
          page: { ...prev.page, value: "1" },
          onlyUserActiveOffers: {
            ...prev.onlyUserActiveOffers,
            value: updated,
          },
        }));
      } else {
        setIsLoginRequiredModalOpen(true);
      }
    },
    [isAuthenticated, posthog, setUiParams],
  );

  const handleToggleOnlyYourListings = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isAuthenticated) {
        const checked = e.target.checked;

        posthog?.capture(EXCHANGE_FILTER_SORT, {
          actionType: checked ? "checked" : "unchecked",
          filterBy: "Only show your listings",
        });

        setUiParams(prev => {
          if (checked) {
            return {
              ...prev,
              page: { ...prev.page, value: "1" },
              onlyYourListings: {
                key: "seller_id",
                value: [String(userId)],
                option: "filter",
                requiresLogin: true,
                omitToDisable: true,
              },
            };
          }
          const updated: UiParams = {
            ...prev,
            page: { ...prev.page, value: "1" },
          };
          delete updated.onlyYourListings;
          return updated;
        });
      } else {
        setIsLoginRequiredModalOpen(true);
      }
    },
    [isAuthenticated, userId, posthog, setUiParams],
  );

  const handleToggleOnlyFilledListings = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isAuthenticated) {
        const checked = e.target.checked;

        posthog?.capture(EXCHANGE_FILTER_SORT, {
          actionType: checked ? "checked" : "unchecked",
          filterBy: "Only show filled listings",
        });

        setUiParams(prev => {
          if (checked) {
            const result: UiParams = {
              ...prev,
              page: { ...prev.page, value: "1" },
              "state.in": {
                key: "state.in",
                value: [...LISTING_DEAL_STATES, "closed"],
                option: "filter",
              },
            };
            return result;
          }

          const updated: UiParams = {
            ...prev,
            page: { ...prev.page, value: "1" },
          };
          delete updated["state.in"];
          return updated;
        });
      } else {
        setIsLoginRequiredModalOpen(true);
      }
    },
    [isAuthenticated, posthog, setUiParams],
  );

  return (
    <>
      {isLoginRequiredModalOpen && (
        <LogInRequiredModal
          open
          onClose={() => setIsLoginRequiredModalOpen(false)}
        />
      )}
      <Box className={classes.filterResetContainer}>
        <Typography variant="h5" className={classes.filterByTitle}>
          Filter By
        </Typography>
        {!hideResetButton && (
          <ResetButton
            disabled={isDefaultUiParams}
            handleReset={handleResetFilters}
          />
        )}
      </Box>
      {!hideOnlySavedListings && (
        <Switch
          checked={uiParams.onlyFavorited?.value[0] === "1"}
          label="Only Favorite Listings"
          onChange={handleToggleOnlySavedListings}
        />
      )}
      {!hideOnlyOfferedListings && (
        <Switch
          checked={uiParams.onlyOffered?.value[0] === "1"}
          label="Only My Offers"
          onChange={handleToggleOnlyOfferedListings}
        />
      )}
      {!hideOnlyActiveOfferListings && (
        <Switch
          checked={uiParams.onlyUserActiveOffers?.value[0] === "1"}
          label="Only My Active Offers"
          onChange={handleToggleOnlyActiveOfferListings}
        />
      )}
      {!hideOnlyYourListings && (
        <Switch
          checked={!!uiParams.onlyYourListings}
          label="Only My Listings"
          onChange={handleToggleOnlyYourListings}
        />
      )}
      {!hideOnlyFilledListings && (
        <Switch
          checked={isEqual(uiParams["state.in"]?.value, [
            ...LISTING_DEAL_STATES,
            "closed",
          ])}
          label="Only Filled Listings"
          onChange={handleToggleOnlyFilledListings}
        />
      )}
    </>
  );
};

export default ToggleFilterGroup;
